import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// --- DOUBLE CHEVRON ANIMATED CONNECTOR ---
const HierarchyConnector = ({ delay }) => (
    <div className="d-flex justify-content-center slide-up-element hierarchy-connector" style={{ animationDelay: delay }}>
        <div className="chevron-arrow-wrapper">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" className="chevron-1">
                <path d="M4 8L12 15L20 8" stroke="#3b82f6" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" className="chevron-2">
                <path d="M4 8L12 15L20 8" stroke="#3b82f6" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        </div>
    </div>
);

const PartyCader = () => {
    const navigate = useNavigate();
    const { appId, language } = useAppId();
    
    const lang = language || 'en';

    const t = {
        en: {
            pageTitle: "Party Cader",
            subtitle: "Organizational Hierarchy Overview",
            saktiKendra: "Sakti Kendra",
            boothPramukh: "Booth Pramukh",
            karyakarta: "Karyakarta",
            mandalPramukh: "Mandal Pramukh",
            panaPramukh: "Pana Pramukh",
            total: "Total",
            allocated: "Allocated",
            pending: "Pending",
            progress: "Allocation Progress",
            back: "Back to Dashboard",
            loading: "Loading Dashboard...",
            totalCadre: "Total Party Strength",
            viewDetails: "View Details",
            levels: "5 Organizational Levels"
        },
        hi: {
            pageTitle: "पार्टी कार्यकर्ता",
            subtitle: "संगठनात्मक पदानुक्रम अवलोकन",
            saktiKendra: "शक्ति केंद्र",
            boothPramukh: "बूथ प्रमुख",
            karyakarta: "कार्यकर्ता",
            mandalPramukh: "मंडल प्रमुख",
            panaPramukh: "पाना प्रमुख",
            total: "कुल",
            allocated: "आवंटित",
            pending: "लंबित",
            progress: "आवंटन प्रगति",
            back: "डैशबोर्ड पर वापस",
            loading: "डैशबोर्ड लोड हो रहा है...",
            totalCadre: "कुल पार्टी शक्ति",
            viewDetails: "विवरण देखें",
            levels: "5 संगठनात्मक स्तर"
        },
        gu: {
            pageTitle: "પાર્ટી કાર્યકર્તા",
            subtitle: "સંગઠનાત્મક વંશવેલો ઝાંખી",
            saktiKendra: "શક્તિ કેન્દ્ર",
            boothPramukh: "બૂથ પ્રમુખ",
            karyakarta: "કાર્યકર્તા",
            mandalPramukh: "મંડળ પ્રમુખ",
            panaPramukh: "પાના પ્રમુખ",
            total: "કુલ",
            allocated: "ફાળવેલ",
            pending: "બાકી",
            progress: "ફાળવણી પ્રગતિ",
            back: "ડેશબોર્ડ પર પાછા",
            loading: "ડેશબોર્ડ લોડ થઈ રહ્યું છે...",
            totalCadre: "કુલ પાર્ટી શક્તિ",
            viewDetails: "વિગતો જુઓ",
            levels: "5 સંગઠનાત્મક સ્તર"
        }
    };

    const txt = t[lang] || t.en;

    const [loading, setLoading] = useState(true);
    const [caderData, setCaderData] = useState({
        saktiKendra: { total: 0 },
        boothPramukh: { total: 0, allocated: 0, pending: 0 },
        karyakarta: { total: 0 },
        mandalPramukh: { total: 8900 },
        panaPramukh: { total: 23400 }
    });

    useEffect(() => {
        if (appId) {
            fetchPartyCadreData();
        }
    }, [appId]);

    const fetchPartyCadreData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_party_cadre_dash`,
                { app_id: appId }
            );

            if (response.data.success && response.data.data && response.data.data.Success === "1") {
                const result = response.data.data.result[0];
                
                setCaderData({
                    saktiKendra: { total: result.total_shaktikendra || 0 },
                    boothPramukh: { 
                        total: result.total_booth || 0,
                        allocated: result.total_booth_with_pramukh || 0,
                        pending: result.remain_booth || 0
                    },
                    karyakarta: { total: result.total_karyakarta || 0 },
                    mandalPramukh: { total: 8900 },
                    panaPramukh: { total: 23400 }
                });
            }
        } catch (error) {
            console.error("Error fetching party cadre data:", error);
        } finally {
            setLoading(false);
        }
    };

    const calculateProgress = (allocated, total) => {
        if (!total || total === 0) return 0;
        return Math.round((allocated / total) * 100);
    };

    const getTotalCadre = () => {
        return (
            caderData.saktiKendra.total +
            caderData.boothPramukh.total +
            caderData.karyakarta.total +
            caderData.mandalPramukh.total +
            caderData.panaPramukh.total
        );
    };

    if (loading) {
        return (
            <div className="party-cader-container">
                <div className="header-wrapper">
                    <Header title={txt.pageTitle} />
                </div>
                <div className="loader-wrapper">
                    <div className="premium-spinner">
                        <div className="spinner-ring"></div>
                        <div className="spinner-ring"></div>
                        <div className="spinner-ring"></div>
                    </div>
                    <p className="loader-text">{txt.loading}</p>
                </div>
                <style jsx>{`
                    .party-cader-container {
                        width: 100%;
                        min-height: 100vh;
                        background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
                        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                    }
                    .header-wrapper {
                        background: white;
                        padding: 0px 24px;
                        position: sticky;
                        top: 0;
                        z-index: 40;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    }
                    .loader-wrapper {
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                        min-height: 70vh;
                        width: 100%;
                        gap: 24px;
                    }
                    .premium-spinner {
                        position: relative;
                        width: 70px;
                        height: 70px;
                    }
                    .spinner-ring {
                        position: absolute;
                        width: 100%;
                        height: 100%;
                        border-radius: 50%;
                        border: 3px solid transparent;
                    }
                    .spinner-ring:nth-child(1) {
                        border-top-color: #6366f1;
                        animation: spin 1.2s linear infinite;
                    }
                    .spinner-ring:nth-child(2) {
                        border-right-color: #8b5cf6;
                        animation: spin 1.2s linear infinite reverse;
                        width: 80%;
                        height: 80%;
                        top: 10%;
                        left: 10%;
                    }
                    .spinner-ring:nth-child(3) {
                        border-bottom-color: #ec4899;
                        animation: spin 1.5s linear infinite;
                        width: 60%;
                        height: 60%;
                        top: 20%;
                        left: 20%;
                    }
                    .loader-text {
                        color: #64748b;
                        font-weight: 600;
                        font-size: 1rem;
                        letter-spacing: 0.3px;
                    }
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="party-cader-container">
            <div className="header-wrapper">
                <Header title={txt.pageTitle} />
            </div>

            <div className="content-container mx-auto" style={{ maxWidth: '1100px' }}>

              

                <div className="cards-wrapper">
                    
                    {/* --- 1. MANDAL PRAMUKH CARD --- */}
                    <div className="cader-card slide-up-element" style={{ animationDelay: '0.1s' }} onClick={() => navigate('#')}>
                        <div className="card-glow mp-glow"></div>
                        <div className="card-top-accent mp-accent"></div>
                        
                        <div className="card-inner-padding single-stat-padding">
                            <div className="single-stat-layout">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="title-icon-wrapper mp-icon-bg">
                                        <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <span className="card-level-tag">Level 01</span>
                                        <h2 className="cader-title mb-0">{txt.mandalPramukh}</h2>
                                    </div>
                                </div>
                                <div className="single-stat-right">
                                    <span className="stat-number-big mp-text">
                                        {caderData.mandalPramukh.total.toLocaleString()}
                                    </span>
                                    <div className="view-details-hint">
                                        {txt.viewDetails}
                                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <HierarchyConnector delay="0.15s" />

                    {/* --- 2. SAKTI KENDRA CARD --- */}
                    <div className="cader-card slide-up-element" style={{ animationDelay: '0.2s' }} onClick={() => navigate('/dis_saktikendra')}>
                        <div className="card-glow sk-glow"></div>
                        <div className="card-top-accent sk-accent"></div>
                        
                        <div className="card-inner-padding single-stat-padding">
                            <div className="single-stat-layout">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="title-icon-wrapper sk-icon-bg">
                                        <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                    </div>
                                    <div>
                                        <span className="card-level-tag">Level 02</span>
                                        <h2 className="cader-title mb-0">{txt.saktiKendra}</h2>
                                    </div>
                                </div>
                                <div className="single-stat-right">
                                    <span className="stat-number-big sk-text">
                                        {caderData.saktiKendra.total.toLocaleString()}
                                    </span>
                                    <div className="view-details-hint">
                                        {txt.viewDetails}
                                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <HierarchyConnector delay="0.25s" />

                    {/* --- 3. BOOTH PRAMUKH CARD --- */}
                    <div className="cader-card featured-card slide-up-element" style={{ animationDelay: '0.3s' }} onClick={() => navigate('/booth_pramukh')}>
                        <div className="card-glow bp-glow"></div>
                        <div className="card-top-accent bp-accent"></div>
                        
                        <div className="card-inner-padding">
                            <div className="card-header-flex">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="title-icon-wrapper bp-icon-bg">
                                        <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <span className="card-level-tag">Level 03</span>
                                        <h2 className="cader-title mb-0">{txt.boothPramukh}</h2>
                                    </div>
                                </div>
                                <div className="view-details-hint desktop-only">
                                    {txt.viewDetails}
                                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </div>

                            <div className="stats-grid">
                                <div className="stat-box stat-total">
                                    <div className="stat-box-icon">
                                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                                        </svg>
                                    </div>
                                    <span className="stat-label">{txt.total}</span>
                                    <span className="stat-number">{caderData.boothPramukh.total.toLocaleString()}</span>
                                </div>
                                <div className="stat-box stat-allocated">
                                    <div className="stat-box-icon">
                                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span className="stat-label">{txt.allocated}</span>
                                    <span className="stat-number">{caderData.boothPramukh.allocated.toLocaleString()}</span>
                                </div>
                                <div className="stat-box stat-pending">
                                    <div className="stat-box-icon">
                                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <span className="stat-label">{txt.pending}</span>
                                    <span className="stat-number">{caderData.boothPramukh.pending.toLocaleString()}</span>
                                </div>
                            </div>

                            <div className="progress-section">
                                <div className="progress-header">
                                    <span className="progress-title">{txt.progress}</span>
                                    <span className="progress-percentage bp-text">
                                        {calculateProgress(caderData.boothPramukh.allocated, caderData.boothPramukh.total)}%
                                    </span>
                                </div>
                                <div className="progress-track">
                                    <div 
                                        className="progress-fill bp-fill" 
                                        style={{ width: `${calculateProgress(caderData.boothPramukh.allocated, caderData.boothPramukh.total)}%` }}
                                    >
                                        <div className="progress-shine"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <HierarchyConnector delay="0.35s" />

                    {/* --- 4. PANA PRAMUKH CARD --- */}
                    <div className="cader-card slide-up-element" style={{ animationDelay: '0.4s' }} onClick={() => navigate('#')}>
                        <div className="card-glow pp-glow"></div>
                        <div className="card-top-accent pp-accent"></div>
                        
                        <div className="card-inner-padding single-stat-padding">
                            <div className="single-stat-layout">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="title-icon-wrapper pp-icon-bg">
                                        <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <span className="card-level-tag">Level 04</span>
                                        <h2 className="cader-title mb-0">{txt.panaPramukh}</h2>
                                    </div>
                                </div>
                                <div className="single-stat-right">
                                    <span className="stat-number-big pp-text">
                                        {caderData.panaPramukh.total.toLocaleString()}
                                    </span>
                                    <div className="view-details-hint">
                                        {txt.viewDetails}
                                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <HierarchyConnector delay="0.45s" />

                    {/* --- 5. KARYAKARTA CARD --- */}
                    <div className="cader-card slide-up-element" style={{ animationDelay: '0.5s' }} onClick={() => navigate('/display_karyakarta')}>
                        <div className="card-glow kar-glow"></div>
                        <div className="card-top-accent kar-accent"></div>
                        
                        <div className="card-inner-padding single-stat-padding">
                            <div className="single-stat-layout">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="title-icon-wrapper kar-icon-bg">
                                        <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <span className="card-level-tag">Level 05</span>
                                        <h2 className="cader-title mb-0">{txt.karyakarta}</h2>
                                    </div>
                                </div>
                                <div className="single-stat-right">
                                    <span className="stat-number-big kar-text">
                                        {caderData.karyakarta.total.toLocaleString()}
                                    </span>
                                    <div className="view-details-hint">
                                        {txt.viewDetails}
                                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <style jsx>{`
                .party-cader-container {
                    width: 100%;
                    min-height: 100vh;
                    background: linear-gradient(180deg, #f8fafc 0%, #eef1f6 100%);
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                    padding-bottom: 60px;
                }

                .header-wrapper {
                    background: white;
                    padding: 0px 24px;
                    position: sticky;
                    top: 0;
                    z-index: 40;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
                }

                .content-container {
                    padding: 24px 20px;
                }

                /* --- HERO SECTION --- */
                .hero-section {
                    position: relative;
                    background: linear-gradient(135deg, #1e1b4b 0%, #312e81 45%, #4c1d95 100%);
                    border-radius: 28px;
                    padding: 36px 40px;
                    margin-bottom: 28px;
                    overflow: hidden;
                    box-shadow: 0 20px 40px -10px rgba(76, 29, 149, 0.35);
                }

                .hero-bg-pattern {
                    position: absolute;
                    top: -50%;
                    right: -10%;
                    width: 400px;
                    height: 400px;
                    background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%);
                    border-radius: 50%;
                }

                .hero-content {
                    position: relative;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    z-index: 1;
                }

                .hero-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(255, 255, 255, 0.12);
                    backdrop-filter: blur(10px);
                    color: #e0e7ff;
                    padding: 6px 14px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 700;
                    letter-spacing: 0.3px;
                    margin-bottom: 16px;
                    border: 1px solid rgba(255,255,255,0.15);
                }

                .hero-title {
                    color: rgba(255, 255, 255, 0.75);
                    font-size: 15px;
                    font-weight: 600;
                    margin: 0 0 20px 0;
                    letter-spacing: 0.2px;
                }

                .hero-total-label {
                    color: rgba(255, 255, 255, 0.6);
                    font-size: 13px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    margin: 0 0 4px 0;
                }

                .hero-total-number {
                    color: #ffffff;
                    font-size: 48px;
                    font-weight: 900;
                    margin: 0;
                    letter-spacing: -1px;
                    line-height: 1;
                    background: linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }

                .hero-icon-container {
                    width: 130px;
                    height: 130px;
                    background: rgba(255, 255, 255, 0.08);
                    backdrop-filter: blur(10px);
                    border-radius: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: rgba(255, 255, 255, 0.9);
                    border: 1px solid rgba(255,255,255,0.12);
                }

                .cards-wrapper {
                    display: flex;
                    flex-direction: column;
                    gap: 0px;
                }

                /* --- Chevron Arrow Connector (NEW) --- */
                .hierarchy-connector {
                    margin: 6px 0;
                }

                .chevron-arrow-wrapper {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 0px;
                    height: 34px;
                }

                .chevron-1,
                .chevron-2 {
                    position: absolute;
                    animation: chevronBounce 1.4s ease-in-out infinite;
                }

                .chevron-1 {
                    opacity: 1;
                    animation-delay: 0s;
                }

                .chevron-2 {
                    opacity: 0.45;
                    margin-top: -14px;
                    animation-delay: 0.15s;
                }

                .chevron-arrow-wrapper {
                    position: relative;
                }

                @keyframes chevronBounce {
                    0%, 100% {
                        transform: translateY(0px);
                        opacity: 1;
                    }
                    50% {
                        transform: translateY(6px);
                        opacity: 0.5;
                    }
                }

                /* --- Card Base --- */
                .cader-card {
                    background: #ffffff; 
                    border-radius: 24px; 
                    box-shadow: 0 4px 20px -4px rgba(15, 23, 42, 0.06);
                    border: 1px solid #eef1f6; 
                    transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
                    position: relative;
                    overflow: hidden;
                    cursor: pointer;
                    z-index: 1;
                }
                .cader-card:hover {
                    box-shadow: 0 24px 40px -12px rgba(15, 23, 42, 0.15);
                    transform: translateY(-4px);
                }
                .cader-card:hover .card-glow {
                    opacity: 1;
                }
                .cader-card:hover .view-details-hint {
                    gap: 8px;
                }

                .card-glow {
                    position: absolute;
                    top: -40%;
                    right: -20%;
                    width: 220px;
                    height: 220px;
                    border-radius: 50%;
                    opacity: 0;
                    transition: opacity 0.4s ease;
                    pointer-events: none;
                    filter: blur(10px);
                }
                .sk-glow { background: radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%); }
                .mp-glow { background: radial-gradient(circle, rgba(245,158,11,0.12) 0%, transparent 70%); }
                .pp-glow { background: radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%); }
                .bp-glow { background: radial-gradient(circle, rgba(14,165,233,0.12) 0%, transparent 70%); }
                .kar-glow { background: radial-gradient(circle, rgba(236,72,153,0.12) 0%, transparent 70%); }

                /* Theme Colors */
                .sk-accent { background: linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%); }
                .sk-icon-bg { background: linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%); color: #3b82f6; }
                .sk-text { color: #3b82f6; }

                .mp-accent { background: linear-gradient(90deg, #f59e0b 0%, #f97316 100%); }
                .mp-icon-bg { background: linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%); color: #d97706; }
                .mp-text { color: #d97706; }

                .pp-accent { background: linear-gradient(90deg, #10b981 0%, #059669 100%); }
                .pp-icon-bg { background: linear-gradient(135deg, #d1fae5 0%, #ecfdf5 100%); color: #059669; }
                .pp-text { color: #059669; }

                .bp-accent { background: linear-gradient(90deg, #0ea5e9 0%, #14b8a6 100%); }
                .bp-icon-bg { background: linear-gradient(135deg, #cffafe 0%, #f0fdfa 100%); color: #0d9488; }
                .bp-fill { background: linear-gradient(90deg, #0ea5e9 0%, #0d9488 100%); }
                .bp-text { color: #0d9488; }

                .kar-accent { background: linear-gradient(90deg, #ec4899 0%, #f43f5e 100%); }
                .kar-icon-bg { background: linear-gradient(135deg, #fce7f3 0%, #fdf2f8 100%); color: #e11d48; }
                .kar-text { color: #e11d48; }

                .card-top-accent {
                    position: absolute; top: 0; left: 0; width: 100%; height: 4px;
                }

                .card-inner-padding {
                    padding: 30px 32px;
                    position: relative;
                    z-index: 1;
                }
                
                .single-stat-padding {
                    padding: 26px 32px;
                }

                .card-header-flex {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    margin-bottom: 28px;
                    padding-bottom: 20px;
                    border-bottom: 1px dashed #e2e8f0;
                }

                .title-icon-wrapper {
                    width: 54px;
                    height: 54px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .card-level-tag {
                    display: block;
                    font-size: 11px;
                    font-weight: 700;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    margin-bottom: 2px;
                }

                .cader-title {
                    font-size: 22px;
                    font-weight: 800;
                    color: #0f172a;
                    margin: 0;
                    letter-spacing: -0.3px;
                }

                /* Single Stat Layout */
                .single-stat-layout {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 16px;
                }
                .single-stat-right {
                    text-align: right;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 6px;
                }

                .stat-number-big {
                    font-size: 34px;
                    font-weight: 900;
                    line-height: 1;
                    letter-spacing: -0.5px;
                }

                .view-details-hint {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 12px;
                    font-weight: 700;
                    color: #94a3b8;
                    transition: gap 0.3s ease;
                }

                .desktop-only {
                    display: flex;
                }

                /* --- Stats Grid --- */
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 16px;
                    margin-bottom: 24px;
                }

                .stat-box {
                    padding: 18px 16px;
                    border-radius: 18px;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 8px;
                    border: 1px solid transparent;
                    position: relative;
                }

                .stat-box-icon {
                    width: 30px;
                    height: 30px;
                    border-radius: 9px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(255,255,255,0.6);
                }

                .stat-label {
                    font-size: 12px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .stat-number {
                    font-size: 26px;
                    font-weight: 800;
                    line-height: 1.1;
                }

                .stat-total { background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border-color: #e2e8f0; }
                .stat-total .stat-box-icon { color: #64748b; }
                .stat-total .stat-label { color: #64748b; }
                .stat-total .stat-number { color: #0f172a; }

                .stat-allocated { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border-color: #bbf7d0; }
                .stat-allocated .stat-box-icon { color: #16a34a; }
                .stat-allocated .stat-label { color: #166534; }
                .stat-allocated .stat-number { color: #15803d; }

                .stat-pending { background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%); border-color: #fed7aa; }
                .stat-pending .stat-box-icon { color: #ea580c; }
                .stat-pending .stat-label { color: #9a3412; }
                .stat-pending .stat-number { color: #ea580c; }

                /* --- Progress Bar --- */
                .progress-section {
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    padding: 18px 20px;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                }

                .progress-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 10px;
                }

                .progress-title {
                    font-size: 13px;
                    font-weight: 700;
                    color: #475569;
                }

                .progress-percentage {
                    font-size: 15px;
                    font-weight: 800;
                }

                .progress-track {
                    width: 100%;
                    height: 10px;
                    background: #e2e8f0;
                    border-radius: 50px;
                    overflow: hidden;
                    box-shadow: inset 0 1px 2px rgba(0,0,0,0.06);
                }

                .progress-fill {
                    height: 100%;
                    border-radius: 50px;
                    transition: width 1.2s cubic-bezier(0.16, 1, 0.3, 1);
                    position: relative;
                    overflow: hidden;
                }

                .progress-shine {
                    position: absolute;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
                    animation: shine 2s infinite;
                }

                @keyframes shine {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(200%); }
                }

                /* Animation */
                .slide-up-element { 
                    animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
                    opacity: 0;
                }
                
                @keyframes slideUp { 
                    from { opacity: 0; transform: translateY(24px); } 
                    to { opacity: 1; transform: translateY(0); } 
                }

                /* --- Responsive --- */
                @media (max-width: 768px) {
                    .content-container {
                        padding: 16px 14px;
                    }

                    .hero-section {
                        padding: 28px 24px;
                        border-radius: 22px;
                    }

                    .hero-total-number {
                        font-size: 36px;
                    }

                    .hero-icon-container {
                        width: 90px;
                        height: 90px;
                        border-radius: 24px;
                    }
                    .hero-icon-container svg {
                        width: 60px;
                        height: 60px;
                    }

                    .desktop-only {
                        display: none;
                    }

                    .stats-grid {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 10px;
                    }

                    .card-inner-padding {
                        padding: 22px 18px;
                    }
                    
                    .single-stat-padding {
                        padding: 20px 18px;
                    }

                    .cader-title {
                        font-size: 18px;
                    }
                    
                    .title-icon-wrapper {
                        width: 44px; height: 44px;
                        border-radius: 13px;
                    }

                    .stat-box {
                        padding: 12px 10px;
                        gap: 6px;
                    }
                    
                    .stat-number {
                        font-size: 20px;
                    }

                    .stat-label {
                        font-size: 10px;
                    }

                    .stat-box-icon {
                        width: 24px; height: 24px;
                    }

                    .stat-number-big {
                        font-size: 26px;
                    }

                    .single-stat-layout {
                        flex-direction: row;
                        flex-wrap: wrap;
                        align-items: center;
                    }
                }

                @media (max-width: 480px) {
                    .hero-content {
                        flex-direction: row;
                        align-items: center;
                    }

                    .hero-total-number {
                        font-size: 30px;
                    }

                    .hero-icon-container {
                        width: 70px;
                        height: 70px;
                        border-radius: 20px;
                    }
                    .hero-icon-container svg {
                        width: 44px;
                        height: 44px;
                    }

                    .stats-grid {
                        gap: 8px;
                    }
                    
                    .stat-box {
                        padding: 10px 8px;
                    }
                    
                    .stat-number {
                        font-size: 17px;
                    }
                    
                    .stat-label {
                        font-size: 9px;
                    }
                    
                    .cader-title {
                        font-size: 16px;
                    }
                    
                    .title-icon-wrapper {
                        width: 38px;
                        height: 38px;
                    }
                    
                    .single-stat-layout {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                    .single-stat-right {
                        align-items: flex-start;
                        width: 100%;
                    }
                    .stat-number-big {
                        font-size: 24px;
                    }
                }
            `}</style>
        </div>
    );
};

export default PartyCader;